/**
 * AI-resources discovery — finds ai-assets directories and monorepo root
 * by walking upward from a starting directory.
 */

import { existsSync, readdirSync, statSync } from "node:fs";
import { basename, dirname, join, parse as parsePath, resolve } from "node:path";

/**
 * Directory names that hold assets of a kind directly under a content root
 * (the *flat* layout, e.g. `<root>/skills/…`). They are never domain folders in
 * the *domain-nested* layout (`<root>/<domain>/skills/…`). The single source of
 * truth shared by every asset/flow walker so the two layouts are recognised
 * identically everywhere.
 *
 * @docLink packages/core/concepts#asset-layout
 */
export const ASSET_KIND_DIRS: ReadonlySet<string> = new Set([
  "skills",
  "flows",
  "agents",
  "prompts",
  "bundles",
  "connectors",
]);

/**
 * Resolve the directories to search for `kind` assets under a content `root`,
 * covering both layouts: the flat `<root>/<kind>/` and each domain-nested
 * `<root>/<domain>/<kind>/`. Each entry carries its domain label (the root's
 * own basename for the flat layout, the subdir name for nested). Existence is
 * NOT checked — callers `existsSync`/read each dir. Dot-dirs, `node_modules`,
 * `external`, `tests`, and {@link ASSET_KIND_DIRS} are never treated as domains.
 *
 * The one layout primitive behind `findSkills`, `loadAllFlows`, and
 * `findFlowFile`; adding a *content-root* layout or asset kind is a change here
 * only. Layouts **within** a search dir are the walker's own business — flows,
 * for instance, also accept a per-flow directory (`flows/<id>/<id>.flow.yaml`),
 * which lives in the flow loader.
 *
 * @docLink packages/core/concepts#asset-layout
 */
export function assetSearchDirs(
  root: string,
  kind: string,
): Array<{ dir: string; domain: string }> {
  const out: Array<{ dir: string; domain: string }> = [
    { dir: join(root, kind), domain: basename(root) },
  ];
  let entries: import("node:fs").Dirent<string>[];
  try {
    entries = readdirSync(root, { withFileTypes: true });
  } catch {
    return out;
  }
  for (const e of entries) {
    if (
      !e.isDirectory() ||
      e.name.startsWith(".") ||
      e.name === "node_modules" ||
      e.name === "external" ||
      e.name === "tests" ||
      ASSET_KIND_DIRS.has(e.name)
    )
      continue;
    out.push({ dir: join(root, e.name, kind), domain: e.name });
  }
  return out;
}

/**
 * Walk upward from `startDir` to find an `ai-assets/` subdirectory.
 *
 * @param startDir - Starting directory for the upward walk
 * @param maxLevels - Maximum number of parent levels to check (default: 10)
 * @returns Absolute path to the `ai-assets/` directory, or `null` if not found
 * @docLink packages/core/concepts#detect-ai-resources
 */
export function detectAiResources(startDir: string, maxLevels = 10): string | null {
  let dir = resolve(startDir);
  const root = parsePath(dir).root;

  for (let i = 0; i < maxLevels; i++) {
    const candidate = join(dir, "ai-assets");
    if (existsSync(candidate) && statSync(candidate).isDirectory()) {
      return candidate;
    }
    const parent = dirname(dir);
    if (parent === dir || parent === root) break;
    dir = parent;
  }
  return null;
}

/**
 * Walk upward from `startDir` to find the monorepo root directory.
 * Uses `pnpm-workspace.yaml` as the marker file.
 *
 * @param startDir - Starting directory for the upward walk
 * @param maxLevels - Maximum number of parent levels to check (default: 10)
 * @returns Absolute path to the monorepo root, or `null` if not found
 * @docLink packages/core/concepts#detect-monorepo-root
 */
export function detectMonorepoRoot(startDir: string, maxLevels = 10): string | null {
  let dir = resolve(startDir);
  const root = parsePath(dir).root;

  for (let i = 0; i < maxLevels; i++) {
    if (existsSync(join(dir, "pnpm-workspace.yaml"))) {
      return dir;
    }
    const parent = dirname(dir);
    if (parent === dir || parent === root) break;
    dir = parent;
  }
  return null;
}

/**
 * Detect all domain directories within an `ai-assets/` root.
 * A domain qualifies when it contains at least one of `skills/`, `flows/`, or `agents/`.
 * Directories starting with `.` and `node_modules` are excluded.
 *
 * @param aiResourcesDir - Absolute path to the `ai-assets/` root
 * @returns Sorted array of domain descriptors with `id`, `path`, and content flags
 * @docLink packages/core/concepts#detect-domains
 */
export function detectDomains(
  aiResourcesDir: string,
): Array<{ id: string; path: string; hasFlows: boolean; hasSkills: boolean; hasAgents: boolean }> {
  const domains: Array<{
    id: string;
    path: string;
    hasFlows: boolean;
    hasSkills: boolean;
    hasAgents: boolean;
  }> = [];

  let entries: import("node:fs").Dirent<string>[];
  try {
    entries = readdirSync(aiResourcesDir, { withFileTypes: true });
  } catch {
    return domains;
  }

  for (const e of entries) {
    if (
      !e.isDirectory() ||
      e.name.startsWith(".") ||
      e.name === "node_modules" ||
      e.name === "external"
    )
      continue;
    const domainPath = join(aiResourcesDir, e.name);
    const hasFlows = existsSync(join(domainPath, "flows"));
    const hasSkills = existsSync(join(domainPath, "skills"));
    const hasAgents = existsSync(join(domainPath, "agents"));
    if (hasFlows || hasSkills || hasAgents) {
      domains.push({ id: e.name, path: domainPath, hasFlows, hasSkills, hasAgents });
    }
  }

  return domains.sort((a, b) => a.id.localeCompare(b.id));
}
